import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type Plugin,
} from "chart.js";
import { ChevronRightIcon, ChevronsUpDownIcon, SearchIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { InputGroup, InputGroupAddon, InputGroupInput } from "~/components/ui/input-group";
import { Label } from "~/components/ui/label";
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from "~/components/ui/popover";
import { Switch } from "~/components/ui/switch";
import { cn } from "~/lib/utils";

import {
  AccountPanelHeading,
  AccountTableFrame,
  ColumnHelp,
  MetricStack,
  SortHeading,
  accountBodyCellClass,
  accountHeadCellClass,
  count,
  percent,
  statusPillClass,
} from "./accountManagementShared";
import type {
  OnboardingClinic,
  OnboardingHealthResponse,
  OnboardingRamp,
  OnboardingStage,
  OnboardingSummary,
  RampClinic,
} from "./accountManagementTypes";

ChartJS.register(LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const rampLabelsPlugin: Plugin = {
  id: "accountRampLabels",
  afterDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const labels: Array<{
      dataset: { clinicName?: string; labelColor?: string; labelMuted?: boolean };
      endpoint: { x: number; y: number };
    }> = [];
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const typed = dataset as typeof dataset & {
        showEndpointLabel?: boolean;
        clinicName?: string;
        labelColor?: string;
        labelMuted?: boolean;
      };
      if (!typed.showEndpointLabel) return;
      const endpoint = chart.getDatasetMeta(datasetIndex).data.at(-1) as
        | { x: number; y: number }
        | undefined;
      if (endpoint) labels.push({ dataset: typed, endpoint });
    });
    if (!labels.length) return;

    labels.sort((left, right) => left.endpoint.y - right.endpoint.y);
    const minGap = labels.length > 12 ? 10 : 13;
    let nextY = chartArea.top + 8;
    const placedLabels = labels.map((label) => {
      const y = Math.max(label.endpoint.y, nextY);
      nextY = y + minGap;
      return { ...label, labelY: y };
    });
    const overflow = nextY - minGap - (chartArea.bottom - 8);
    if (overflow > 0) {
      for (let index = placedLabels.length - 1; index >= 0; index -= 1) {
        const maxY =
          index === placedLabels.length - 1
            ? chartArea.bottom - 8
            : placedLabels[index + 1]!.labelY - minGap;
        placedLabels[index]!.labelY = Math.min(placedLabels[index]!.labelY - overflow, maxY);
      }
    }

    ctx.save();
    ctx.font = `${labels.length > 12 ? 7 : 8}px system-ui`;
    ctx.lineWidth = 0.7;
    placedLabels.forEach(({ dataset, endpoint, labelY }) => {
      ctx.fillStyle = dataset.labelMuted ? "#a8b0bc" : dataset.labelColor || "#64748b";
      ctx.strokeStyle = dataset.labelMuted
        ? "rgba(148, 163, 184, .22)"
        : `${dataset.labelColor || "#64748b"}59`;
      ctx.beginPath();
      ctx.moveTo(endpoint.x + 3, endpoint.y);
      ctx.lineTo(chartArea.right + 3, labelY);
      ctx.stroke();
      ctx.fillText(dataset.clinicName || "", chartArea.right + 6, labelY + 2);
    });
    ctx.restore();
  },
};

function clinicSwatchClass(clinic: RampClinic, selected: boolean): string {
  if (clinic.behindCurve) return "bg-red-500";
  if (selected) return "bg-blue-600";
  return "bg-slate-400";
}

function CompareClinicsPicker(props: {
  behindCount: number;
  clinics: RampClinic[];
  matchingClinics: RampClinic[];
  plottedCount: number;
  search: string;
  selectedClinicIds: string[];
  selectedClinicIdSet: Set<string>;
  showBehindCurve: boolean;
  onClear: () => void;
  onSearchChange: (value: string) => void;
  onSelectAll: () => void;
  onShowBehindCurveChange: (checked: boolean) => void;
  onToggle: (clinicId: string) => void;
}) {
  const selectedClinics = props.clinics.filter((clinic) =>
    props.selectedClinicIdSet.has(clinic.id),
  );
  const visibleChips = selectedClinics.slice(0, 6);
  const hiddenChipCount = selectedClinics.length - visibleChips.length;

  return (
    <div className="mt-3 flex flex-col gap-2.5" aria-live="polite">
      <div className="flex flex-wrap items-center gap-2">
        <Popover>
          <PopoverTrigger render={<Button size="sm" variant="outline" />}>
            <ChevronsUpDownIcon className="size-3.5 opacity-80" aria-hidden />
            Compare clinics
            {props.selectedClinicIds.length ? (
              <Badge size="sm" variant="secondary">
                {props.selectedClinicIds.length}
              </Badge>
            ) : null}
          </PopoverTrigger>
          <PopoverPopup align="start" className="w-[22rem]" side="bottom" sideOffset={6}>
            <div className="flex w-full flex-col gap-3">
              <div className="space-y-1">
                <PopoverTitle className="text-sm">Compare clinics</PopoverTitle>
                <p className="text-xs text-muted-foreground">
                  Plot selected clinics against the typical onboarding range.
                </p>
              </div>
              <InputGroup>
                <InputGroupAddon>
                  <SearchIcon />
                </InputGroupAddon>
                <InputGroupInput
                  type="search"
                  size="sm"
                  value={props.search}
                  onChange={(event) => props.onSearchChange(event.target.value)}
                  placeholder="Search clinics"
                  aria-label="Search clinics to compare"
                />
              </InputGroup>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <Button size="xs" variant="ghost" onClick={props.onSelectAll}>
                    Select all
                  </Button>
                  <Button size="xs" variant="ghost" onClick={props.onClear}>
                    Clear
                  </Button>
                </div>
                <span className="text-xs text-muted-foreground">
                  {props.selectedClinicIds.length
                    ? `${props.selectedClinicIds.length} selected`
                    : "None selected"}
                </span>
              </div>
              <fieldset className="max-h-56 min-w-0 overflow-y-auto rounded-md border border-border/60 bg-background/60 p-1">
                <legend className="sr-only">Clinics to compare</legend>
                {props.matchingClinics.map((clinic) => {
                  const selected = props.selectedClinicIdSet.has(clinic.id);
                  return (
                    <label
                      key={clinic.id}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
                        selected ? "bg-accent text-foreground" : "hover:bg-accent/60",
                      )}
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => props.onToggle(clinic.id)}
                        aria-label={clinic.name}
                      />
                      <span
                        className={cn(
                          "size-2 shrink-0 rounded-full",
                          clinicSwatchClass(clinic, selected),
                        )}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate">{clinic.name}</span>
                      {clinic.behindCurve ? (
                        <Badge size="sm" variant="error">
                          Below
                        </Badge>
                      ) : null}
                    </label>
                  );
                })}
                {!props.matchingClinics.length ? (
                  <p className="px-2 py-3 text-xs text-muted-foreground">No clinics found.</p>
                ) : null}
              </fieldset>
            </div>
          </PopoverPopup>
        </Popover>
        <Label className="h-8 cursor-pointer rounded-lg border border-border/70 bg-background px-2.5 font-normal shadow-xs/5">
          <Switch checked={props.showBehindCurve} onCheckedChange={props.onShowBehindCurveChange} />
          Below typical range
          <Badge size="sm" variant={props.showBehindCurve ? "error" : "outline"}>
            {props.behindCount}
          </Badge>
        </Label>
        <span className="text-xs text-muted-foreground">
          {props.plottedCount
            ? `${props.plottedCount} clinic${props.plottedCount === 1 ? "" : "s"} shown`
            : "Select clinics to compare against the typical range."}
        </span>
      </div>
      {visibleChips.length ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {visibleChips.map((clinic) => (
            <button
              key={clinic.id}
              type="button"
              className="inline-flex max-w-48 items-center gap-1.5 rounded-full border border-border/70 bg-background px-2 py-0.5 text-xs text-foreground transition-colors hover:bg-accent"
              onClick={() => props.onToggle(clinic.id)}
              aria-label={`Remove ${clinic.name} from comparison`}
            >
              <span
                className={cn("size-1.5 shrink-0 rounded-full", clinicSwatchClass(clinic, true))}
                aria-hidden
              />
              <span className="truncate">{clinic.name}</span>
              <XIcon className="size-3 opacity-70" aria-hidden />
            </button>
          ))}
          {hiddenChipCount > 0 ? (
            <span className="text-xs text-muted-foreground">+{hiddenChipCount} more</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function OnboardingRampChart({ ramp }: { ramp: OnboardingRamp }) {
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [showBehindCurve, setShowBehindCurve] = useState(false);
  const clinics = useMemo(
    () => [...ramp.clinics].sort((left, right) => left.name.localeCompare(right.name)),
    [ramp.clinics],
  );
  const selectedClinicIdSet = useMemo(() => new Set(selectedClinicIds), [selectedClinicIds]);
  const behindCurveClinics = clinics.filter((clinic) => clinic.behindCurve);
  const plottedClinicIds = useMemo(() => {
    const ids = new Set(selectedClinicIds);
    if (showBehindCurve) {
      behindCurveClinics.forEach((clinic) => {
        ids.add(clinic.id);
      });
    }
    return ids;
  }, [behindCurveClinics, selectedClinicIds, showBehindCurve]);
  const plottedClinics = clinics.filter((clinic) => plottedClinicIds.has(clinic.id));
  const matchingClinics = clinics.filter((clinic) =>
    clinic.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  const newClinicAges = plottedClinics
    .map((clinic) => clinic.daysSinceLaunch)
    .filter((age): age is number => age !== null && age >= 0 && age < 30);
  const dailyMode = newClinicAges.length > 0;
  const dailyAxisMax = dailyMode ? Math.max(...newClinicAges) : 0;

  useEffect(() => {
    setSelectedClinicIds((current) =>
      current.filter((id) => clinics.some((clinic) => clinic.id === id)),
    );
  }, [clinics]);

  function toggleClinic(clinicId: string) {
    setSelectedClinicIds((current) =>
      current.includes(clinicId) ? current.filter((id) => id !== clinicId) : [...current, clinicId],
    );
  }

  const bandPoints = dailyMode
    ? ramp.dailyBand
        .filter((point) => point.day <= dailyAxisMax)
        .map((point) => ({
          x: point.day,
          p25: point.p25,
          p50: point.p50,
          p75: point.p75,
          clinicCount: point.clinicCount,
        }))
    : ramp.band.map((point) => ({
        x: point.month,
        p25: point.p25,
        p50: point.p50,
        p75: point.p75,
        clinicCount: point.clinicCount,
      }));
  const datasets = [
    {
      label: "p75",
      data: bandPoints.map((point) => ({
        x: point.x,
        y: point.clinicCount >= 5 ? point.p75 * 100 : null,
      })),
      borderColor: "transparent",
      backgroundColor: "rgba(61, 119, 168, .16)",
      pointRadius: 0,
    },
    {
      label: "Typical range (p25–p75)",
      data: bandPoints.map((point) => ({
        x: point.x,
        y: point.clinicCount >= 5 ? point.p25 * 100 : null,
      })),
      borderColor: "transparent",
      backgroundColor: "rgba(61, 119, 168, .16)",
      pointRadius: 0,
      fill: "-1",
    },
    {
      label: "Median",
      data: bandPoints.map((point) => ({
        x: point.x,
        y: point.clinicCount >= 5 ? point.p50 * 100 : null,
      })),
      borderColor: "#3d77a8",
      borderDash: [6, 4],
      borderWidth: 2,
      pointRadius: 0,
    },
    ...plottedClinics.map((clinic) => {
      const isSelected = selectedClinicIdSet.has(clinic.id);
      const color = clinic.behindCurve ? "#dc4c35" : "#64748b";
      const selectedColor = clinic.behindCurve ? "#dc4c35" : "#1d4ed8";
      const displayColor = isSelected ? selectedColor : color;
      const data = dailyMode
        ? clinic.dailyPoints
            .filter((point) => point.day <= dailyAxisMax)
            .map((point) => ({ x: point.day, y: point.alignmentRate * 100, point }))
        : clinic.points.map((point) => ({ x: point.month, y: point.alignmentRate * 100, point }));
      return {
        label: clinic.name,
        clinicId: clinic.id,
        clinicName: clinic.name,
        behindCurve: clinic.behindCurve,
        showEndpointLabel: true,
        labelMuted: false,
        labelColor: displayColor,
        data,
        borderColor: displayColor,
        backgroundColor: displayColor,
        borderWidth: isSelected ? 3 : 2.2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.25,
        order: isSelected ? 0 : 1,
      };
    }),
  ];

  return (
    <section className="rounded-lg border border-border/70 bg-background/80 p-4 shadow-xs">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Onboarding health · cohort
          </span>
          <h2>Ramp vs. typical onboarding curve</h2>
        </div>
        <span>alignment by {dailyMode ? "days" : "months"} since first patient</span>
      </div>
      <p className="mb-3 text-sm text-muted-foreground">
        The shaded p25–p75 band shows the typical alignment rate at the same elapsed{" "}
        {dailyMode ? "day" : "month"}; red clinics currently sit below it.
      </p>
      <div className="relative h-[420px] min-w-0">
        <Line
          data={{ datasets }}
          plugins={[rampLabelsPlugin]}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            parsing: false,
            normalized: true,
            onClick: (_event, elements) => {
              const index = elements[0]?.datasetIndex;
              if (index == null) return;
              const clinicId = (datasets[index] as { clinicId?: string } | undefined)?.clinicId;
              if (!clinicId) return;
              toggleClinic(clinicId);
            },
            onHover: (event, elements) => {
              const index = elements[0]?.datasetIndex;
              const clinicId =
                index == null
                  ? undefined
                  : (datasets[index] as { clinicId?: string } | undefined)?.clinicId;
              const target = event.native?.target as HTMLElement | undefined;
              if (target) target.style.cursor = clinicId ? "pointer" : "default";
            },
            interaction: { mode: "nearest", intersect: false },
            layout: { padding: { right: 160, top: 8 } },
            plugins: {
              legend: {
                position: "bottom",
                labels: {
                  filter: (item) => ["Typical range (p25–p75)", "Median"].includes(item.text),
                },
              },
              tooltip: {
                mode: "nearest",
                intersect: false,
                callbacks: {
                  label: (context) => {
                    if (context.dataset.label === "p75") return "";
                    const point = (
                      context.raw as
                        | {
                            point?: {
                              marketablePatients?: number;
                              activePatients?: number;
                              alignedPatients: number;
                              snapshotDate: string;
                            };
                          }
                        | undefined
                    )?.point;
                    const y = context.parsed.y ?? 0;
                    if (!point) {
                      return `${context.dataset.label}: ${y.toFixed(1)}%`;
                    }
                    const marketablePatients = point.marketablePatients ?? point.activePatients;
                    return [
                      `${context.dataset.label}: ${y.toFixed(1)}%`,
                      `${count(point.alignedPatients)} of ${count(marketablePatients)} marketable patients`,
                      `Snapshot: ${point.snapshotDate}`,
                    ];
                  },
                },
              },
            },
            scales: {
              x: {
                type: "linear",
                min: 0,
                max: dailyMode ? Math.max(dailyAxisMax, 1) : undefined,
                title: {
                  display: true,
                  text: dailyMode ? "Days since first patient" : "Months since first patient",
                },
                ticks: { precision: 0 },
                grid: { color: "#eee9df" },
              },
              y: {
                min: 0,
                suggestedMax: 40,
                title: { display: true, text: "Alignment rate" },
                ticks: { callback: (value) => `${value}%` },
                grid: { color: "#eee9df" },
              },
            },
          }}
        />
      </div>
      <CompareClinicsPicker
        behindCount={behindCurveClinics.length}
        clinics={clinics}
        matchingClinics={matchingClinics}
        plottedCount={plottedClinics.length}
        search={search}
        selectedClinicIds={selectedClinicIds}
        selectedClinicIdSet={selectedClinicIdSet}
        showBehindCurve={showBehindCurve}
        onClear={() => setSelectedClinicIds([])}
        onSearchChange={setSearch}
        onSelectAll={() => setSelectedClinicIds(clinics.map((clinic) => clinic.id))}
        onShowBehindCurveChange={setShowBehindCurve}
        onToggle={toggleClinic}
      />
      <small className="mt-2 block text-xs text-muted-foreground">
        Behind = latest snapshot below p25 at the same elapsed day or month. Percentiles are
        calculated server-side across clinics with data at each period.
      </small>
    </section>
  );
}
function OnboardingStagePill({ stage, label }: { stage: OnboardingStage; label: string }) {
  const tone = stage === "target-met" ? "good" : stage === "stalled" ? "at-risk" : "warn";
  return <span className={statusPillClass(tone)}>{label}</span>;
}

function OnboardingFunnel({ summary }: { summary: OnboardingSummary }) {
  const steps = [
    {
      key: "total",
      label: "Marketable patients",
      value: summary.totalPatients,
      rate: 1,
    },
    {
      key: "pipeline",
      label: "Active enrollment",
      value: summary.activePipelinePatients,
      rate: summary.activePipelineRate,
    },
    {
      key: "enrolled",
      label: "Enrolled",
      value: summary.enrolledPatients,
      rate: summary.enrolledRate,
    },
    {
      key: "aligned",
      label: "Aligned",
      value: summary.alignedPatients,
      rate: summary.alignmentRate,
    },
  ];

  return (
    <section className="rounded-lg border border-border/70 bg-background/80 p-5 shadow-xs">
      <AccountPanelHeading meta="warehouse-derived patient funnel">
        Enrollment lifecycle
      </AccountPanelHeading>
      <ol className="grid items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {steps.map((step, index) => (
          <li key={step.key} className="relative min-h-0">
            <article className="flex h-full flex-col gap-2 rounded-lg border border-border/70 bg-background px-4 py-3.5 shadow-xs/5">
              <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                {step.label}
              </span>
              <strong className="text-2xl font-semibold tracking-tight text-foreground tabular-nums">
                {count(step.value)}
              </strong>
              <span className="mt-auto text-xs leading-relaxed text-muted-foreground">
                {percent(step.rate)} of marketable patients
              </span>
            </article>
            {index < steps.length - 1 ? (
              <ChevronRightIcon
                className="pointer-events-none absolute top-1/2 -right-2 hidden size-4 -translate-y-1/2 text-muted-foreground xl:block"
                aria-hidden
              />
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

function BehindBenchmarkBand({
  clinics,
  targetRate,
  minimumPatients,
}: {
  clinics: OnboardingClinic[];
  targetRate: number;
  minimumPatients: number;
}) {
  const stageRank: Record<string, number> = {
    stalled: 0,
    progressing: 1,
    "early-ramp": 2,
    "target-met": 3,
  };
  const behind = [...clinics]
    .filter((clinic) => clinic.behindBenchmark && clinic.totalPatients >= minimumPatients)
    .sort((left, right) => {
      const stageDifference = (stageRank[left.stage] ?? 99) - (stageRank[right.stage] ?? 99);
      if (stageDifference) return stageDifference;
      return right.totalPatients - left.totalPatients;
    });
  const visible = behind.slice(0, 8);

  return (
    <section className="rounded-lg border border-border/70 bg-background/80 p-5 shadow-xs">
      <AccountPanelHeading
        meta={`${behind.length} medium/large clinics below ${percent(targetRate)}`}
      >
        Behind benchmark
      </AccountPanelHeading>
      {visible.length ? (
        <div className="divide-y divide-border/60">
          {visible.map((clinic) => (
            <article
              key={clinic.id}
              className="flex flex-col gap-3 py-3.5 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground" title={clinic.name}>
                  {clinic.name}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {clinic.state} · {count(clinic.totalPatients)} patients · {clinic.stageLabel}
                </p>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
                <span className="mr-1 text-sm font-semibold tabular-nums text-foreground">
                  {percent(clinic.alignmentRate)}
                </span>
                {clinic.reasons.map((item) => (
                  <span
                    key={item}
                    className={
                      clinic.stage === "stalled"
                        ? "rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700"
                        : "rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800"
                    }
                  >
                    {item}
                  </span>
                ))}
                <a
                  href={`#onboarding-${clinic.id}`}
                  className="text-xs font-medium text-foreground underline-offset-2 hover:underline"
                >
                  Review →
                </a>
              </div>
            </article>
          ))}
          {behind.length > visible.length ? (
            <p className="pt-3 text-xs text-muted-foreground">
              +{behind.length - visible.length} more below-benchmark clinics in the tracker
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No medium or large clinics are currently below the alignment benchmark.
        </p>
      )}
    </section>
  );
}

function onboardingSortableValue(clinic: OnboardingClinic, key: string): unknown {
  if (key === "clinic") return clinic.name.toLowerCase();
  if (key === "stage") {
    return (
      { stalled: 0, progressing: 1, "early-ramp": 2, "target-met": 3 } as Record<string, number>
    )[clinic.stage];
  }
  if (key === "patientDates") {
    return clinic.latestPatientAddedDate ?? clinic.firstPatientAddedDate;
  }
  return (clinic as Record<string, unknown>)[key];
}

export type OnboardingSort = { key: string; direction: "asc" | "desc" };

export function sortOnboardingClinics(
  clinics: OnboardingClinic[],
  sort: OnboardingSort,
): OnboardingClinic[] {
  const direction = sort.direction === "asc" ? 1 : -1;
  return [...clinics].sort((left, right) => {
    const leftValue = onboardingSortableValue(left, sort.key);
    const rightValue = onboardingSortableValue(right, sort.key);
    if (leftValue === null || leftValue === undefined) return 1;
    if (rightValue === null || rightValue === undefined) return -1;
    if (typeof leftValue === "string" && typeof rightValue === "string") {
      return leftValue.localeCompare(rightValue) * direction;
    }
    return ((leftValue as number) - (rightValue as number)) * direction;
  });
}

function OnboardingTracker({ clinics }: { clinics: OnboardingClinic[] }) {
  const [sort, setSort] = useState<OnboardingSort>({
    key: "alignmentRate",
    direction: "asc",
  });
  const sorted = useMemo(() => sortOnboardingClinics(clinics, sort), [clinics, sort]);

  function updateSort(key: string) {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  }

  const headings = [
    {
      key: "clinic",
      label: "Clinic",
      definition: "The clinic name, state, and how long it has had patients in Carespace.",
    },
    {
      key: "stage",
      label: "Stage",
      definition:
        "Where the clinic is in onboarding: just starting, making progress, stuck, or at the goal.",
    },
    {
      key: "totalPatients",
      label: "Funnel",
      definition:
        "How many patients can be worked, are being enrolled, are enrolled, and are fully aligned.",
    },
    {
      key: "alignmentRate",
      label: "Alignment",
      definition: "The share of workable patients who are currently aligned with the clinic.",
    },
    {
      key: "alignedLast7d",
      label: "Momentum",
      definition:
        "Recent progress: patients aligned in the last 7 days and patients added in the last 30 days.",
    },
    {
      key: "patientsToTarget",
      label: "To 20%",
      definition:
        "How many more patients must become aligned for the clinic to reach the 20% goal.",
    },
    {
      key: "patientDates",
      label: "Patient dates",
      definition: "When the clinic’s first and most recently added patient records were created.",
    },
  ];

  return (
    <section className="rounded-lg border border-border/70 bg-background/80 p-5 shadow-xs">
      <AccountPanelHeading meta="click a header to sort · warehouse data only">
        All clinics — onboarding tracker
      </AccountPanelHeading>
      <AccountTableFrame aria-label="Onboarding tracker" minWidthClass="min-w-[1280px]">
        <thead>
          <tr>
            {headings.map(({ key, label, definition }, index) => (
              <th
                key={key}
                className={accountHeadCellClass(index === 0)}
                aria-sort={
                  sort.key === key
                    ? sort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                <span className="inline-flex items-center gap-1">
                  <SortHeading column={key} label={label} sort={sort} onSort={updateSort} />
                  <ColumnHelp label={label} definition={definition} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((clinic) => (
            <tr key={clinic.id} id={`onboarding-${clinic.id}`} className="group">
              <td className={accountBodyCellClass({ sticky: true })}>
                <span className="block truncate font-semibold" title={clinic.name}>
                  {clinic.name}
                </span>
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  {clinic.state}
                  {clinic.daysActive === null ? "" : ` · ${count(clinic.daysActive)}d active`}
                </span>
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                <OnboardingStagePill stage={clinic.stage} label={clinic.stageLabel} />
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                <MetricStack
                  nowrap
                  value={`${count(clinic.alignedPatients)} aligned`}
                  detail={`${count(clinic.enrolledPatients)} enrolled · ${count(clinic.activePipelinePatients)} active · ${count(clinic.totalPatients)} marketable`}
                />
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                <span className="font-semibold tabular-nums">{percent(clinic.alignmentRate)}</span>
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                <MetricStack
                  nowrap
                  value={`+${count(clinic.alignedLast7d)} aligned 7d`}
                  detail={`+${count(clinic.patientsAddedLast30d)} new patients 30d`}
                />
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                {count(clinic.patientsToTarget)}
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                <MetricStack
                  nowrap
                  value={clinic.firstPatientAddedDate || "—"}
                  detail={
                    clinic.latestPatientAddedDate
                      ? `Latest ${clinic.latestPatientAddedDate}`
                      : "Latest —"
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </AccountTableFrame>
    </section>
  );
}

export function OnboardingHealthPanel({ data }: { data: OnboardingHealthResponse }) {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-muted-foreground">As of {data.asOf}</span>
      </div>
      <OnboardingRampChart ramp={data.ramp} />
      <OnboardingFunnel summary={data.summary} />
      <BehindBenchmarkBand
        clinics={data.clinics}
        targetRate={data.alignmentTargetRate}
        minimumPatients={data.minimumBenchmarkPatientCount}
      />
      <OnboardingTracker clinics={data.clinics} />
      <p className="text-xs text-muted-foreground">
        Warehouse only. Active enrollment combines non-terminal enrollment statuses with patients
        already enrolled; enrolled follows the existing GUIDE assessed, submitted, pending, or
        aligned contract.
      </p>
    </div>
  );
}
